/**
 * CompanyAdminInfo — Read-only Company Info tab for the Company Admin portal.
 * Displays company details, enabled feature flags, and enabled document types.
 */

import React, { useState, useEffect } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { companyFlags, FEATURE_FLAG_DEFINITIONS } from "../../config/featureFlagDefinitions";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { Building2, Globe, Mail, Phone, FileText, Copy, Check, Shield, ToggleRight } from "lucide-react";

const CompanyAdminInfo = ({ companyId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [allDocumentTypes, setAllDocumentTypes] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companySnap, dtSnap] = await Promise.all([
          getDoc(doc(db, "companies", companyId)),
          getDocs(collection(db, "documentTypes")),
        ]);

        if (companySnap.exists()) {
          setCompany({ id: companySnap.id, ...companySnap.data() });
        }

        const types = dtSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        types.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
        setAllDocumentTypes(types);
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
      setIsLoading(false);
    };

    if (companyId) fetchData();
  }, [companyId]);

  const handleCopyCode = () => {
    if (company?.code) {
      navigator.clipboard.writeText(company.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="standard-container">
        <Box sx={{ width: "100%", mt: 4 }}>
          <LinearProgress />
        </Box>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="standard-container">
        <Typography color="text.secondary" sx={{ mt: 4 }}>
          Company information could not be loaded.
        </Typography>
      </div>
    );
  }

  // Resolve enabled flags
  const flagMap = company.featureFlags || {};
  const enabledDocTypes = company.enabledDocumentTypes; // null = all

  // Build a lookup for flag definitions by key
  const flagDefsByKey = {};
  FEATURE_FLAG_DEFINITIONS.forEach((f) => { flagDefsByKey[f.key] = f; });

  // Info row helper
  const InfoRow = ({ icon: Icon, label, value, chips }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
      {Icon && <Icon size={18} style={{ marginTop: 2, color: "var(--color-text-secondary)", flexShrink: 0 }} />}
      <Box sx={{ minWidth: 140, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
      </Box>
      {chips ? (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {chips}
        </Box>
      ) : (
        <Typography variant="body2">{value || "—"}</Typography>
      )}
    </Box>
  );

  return (
    <div className="standard-container">
      {/* Company Details */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Building2 size={20} /> Company Details
        </Typography>

        <InfoRow icon={Building2} label="Name" value={company.name} />
        <InfoRow icon={Shield} label="Invite Code" value={
          <Box className="company-admin-invite-code-wrap" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <code className="company-admin-invite-code">
              {company.code}
            </code>
            <Tooltip title={copied ? "Copied!" : "Copy code"}>
              <IconButton className="company-admin-invite-copy-btn" size="small" onClick={handleCopyCode}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </IconButton>
            </Tooltip>
          </Box>
        } />
        <InfoRow label="Type" value={
          <Chip
            label={company.type || "standard"}
            size="small"
            color={company.type === "enterprise" ? "primary" : "default"}
            variant="outlined"
          />
        } />
        <InfoRow label="Status" value={
          <Chip
            label={company.isActive !== false ? "Active" : "Inactive"}
            size="small"
            color={company.isActive !== false ? "success" : "error"}
          />
        } />
        {company.domain && <InfoRow icon={Globe} label="Domain" value={company.domain} />}
        {company.email && <InfoRow icon={Mail} label="Email" value={company.email} />}
        {company.phone && <InfoRow icon={Phone} label="Phone" value={company.phone} />}
        {company.description && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{company.description}</Typography>
          </Box>
        )}
      </Paper>

      {/* Available Features */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleRight size={20} /> Available Features
        </Typography>

        {(() => {
          const enabledFlags = companyFlags.filter((flag) => flagMap[flag.key] === true);
          if (enabledFlags.length === 0) {
            return <Typography variant="body2" color="text.secondary">No additional features are enabled for your organization.</Typography>;
          }
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {enabledFlags.map((flag) => (
                <Box
                  key={flag.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "rgba(46,125,50,0.06)",
                    border: "1px solid",
                    borderColor: "rgba(46,125,50,0.2)",
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{flag.label}</Typography>
                    {flag.description && (
                      <Typography variant="caption" color="text.secondary">{flag.description}</Typography>
                    )}
                  </Box>
                  <Chip label="Enabled" size="small" color="success" />
                </Box>
              ))}
            </Box>
          );
        })()}
      </Paper>

      {/* Enabled Document Types */}
      <Paper sx={{ p: 3, mt: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FileText size={20} /> Available Documents
        </Typography>

        {(() => {
          const enabledTypes = allDocumentTypes.filter(
            (dt) => enabledDocTypes === null || enabledDocTypes === undefined || enabledDocTypes.includes(dt.id)
          );
          if (enabledTypes.length === 0) {
            return <Typography variant="body2" color="text.secondary">No document types are enabled for your organization.</Typography>;
          }
          return (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {enabledTypes.map((dt) => (
                <Chip
                  key={dt.id}
                  label={dt.name || dt.id}
                  size="small"
                  color="primary"
                />
              ))}
            </Box>
          );
        })()}
      </Paper>
    </div>
  );
};

export default CompanyAdminInfo;
